import { Injectable, inject } from '@angular/core';
import { EnvironmentKeys } from 'src/environments/environment-keys';
import { ConfigService } from './json-config.service';
import { PostHog } from 'posthog-js'
import { interval, Subscription, take } from 'rxjs';
import { LoggerService } from './logger.service';

export interface PosthogUserInput {
  client_id: string,
  fullName: string,
  email?: string,
}

@Injectable({
  providedIn: 'root'
})
export class PosthogService {
  private configService = inject(ConfigService);
  private loggerService = inject(LoggerService);

  // The snippet is gated by cookie consent and is often blocked (ad blockers).
  // A missing window.posthog is expected and must not be reported to Sentry.
  private posthog?: PostHog = (window as Window & { posthog?: PostHog }).posthog;
  private posthogInitSub?: Subscription;
  private posthogStartLogSub?: Subscription;
  private readonly posthogAssignAttempts = 10;

  // duplicit identify call prevention
  private alreadyLogging = false;

  public init(): void {
    if(!this.posthogConfigExist()) {
      this.loggerService.logInfo('Posthog config missing, not initializing it.');
      return;
    }

    if(!this.posthog) {
      this.assignPosthogInstance();
    } else {
      try {
        this.posthog.init?.(
          this.configService.getConfig(EnvironmentKeys.posthogProjectApiKey),
          {
            api_host: this.configService.getConfig(EnvironmentKeys.posthogInstanceAddress),
            opt_in_site_apps: !!this.configService.getConfig(EnvironmentKeys.posthogOptInSiteApps),
          }
        );
      } catch (error) {
        this.loggerService.error('Failed to initialize Posthog analytic! ', error);
      }

      if(this.posthogInitSub) {
        this.posthogInitSub.unsubscribe();
      }
    }

  }

  private posthogConfigExist(): boolean {
    return !!this.configService.getConfig(EnvironmentKeys.posthogInstanceAddress) &&
    !!this.configService.getConfig(EnvironmentKeys.posthogOptInSiteApps)
  }

  public startLog(user: PosthogUserInput): void {
    if(this.posthog && this.posthogConfigExist() && !this.alreadyLogging && typeof this.posthog.identify === 'function') {
      try {
        this.posthog.identify?.(
          user.client_id,
          {
            name: user.fullName,
            email: user.email
          }
        );
        this.alreadyLogging = true;
      } catch (error) {
        this.loggerService.error('Failed to start Posthog logging! ', error);
        this.alreadyLogging = false;
      }

      if(this.posthogStartLogSub) {
        this.posthogStartLogSub.unsubscribe();
      }
    } else {
      if(this.posthogConfigExist() && !this.alreadyLogging) {
        this.assignPosthogInstance(user);
      }
    }
  }

  /**
   * Watch for window.posthog assigned by the cookie-gated snippet.
   * Stops after a few attempts so a blocked script cannot report forever.
   */
  private assignPosthogInstance(user?: PosthogUserInput): void {
    if(this.assignWatchActive(!!user)) {
      return;
    }

    const sub = interval(1000).pipe(
      take(this.posthogAssignAttempts),
    ).subscribe({
      next: () => this.tryUsePosthogInstance(user),
      complete: () => this.reportMissingPosthog(),
    });

    this.storeAssignWatch(!!user, sub);
  }

  private tryUsePosthogInstance(user?: PosthogUserInput): void {
    const posthogInstance = (window as Window & { posthog?: PostHog }).posthog;
    if(!posthogInstance) {
      return;
    }

    this.posthog = posthogInstance;
    this.stopAssignWatch(!!user);
    if(user) {
      this.startLog(user);
    } else {
      this.init();
    }
  }

  private reportMissingPosthog(): void {
    if(this.posthog) {
      return;
    }
    this.loggerService.logInfo('Posthog script unavailable, analytics skipped.');
  }

  private assignWatchActive(forUser: boolean): boolean {
    const sub = forUser ? this.posthogStartLogSub : this.posthogInitSub;
    return !!sub && !sub.closed;
  }

  private stopAssignWatch(forUser: boolean): void {
    if(forUser) {
      this.posthogStartLogSub?.unsubscribe();
      this.posthogStartLogSub = undefined;
      return;
    }
    this.posthogInitSub?.unsubscribe();
    this.posthogInitSub = undefined;
  }

  private storeAssignWatch(forUser: boolean, sub: Subscription): void {
    if(forUser) {
      this.posthogStartLogSub = sub;
      return;
    }
    this.posthogInitSub = sub;
  }

  public endLog(): void {
    if (this.posthog && this.posthogConfigExist()) {
      try {
        this.posthog.reset?.();
      } catch (error) {
        this.loggerService.error('Failed to close Posthog logging! ', error);
      }
    }
    this.alreadyLogging = false;
  }
}
